// [COPIAR CÓDIGO]
//
/* GS://PROTOCOLO
 * Arquivo: src/components/forms/ProductForm.jsx
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 6.12.0 – FEAT: Seletor de Fornecedor e SKU Dinâmico
 * Impacto da Versão: [src/components/forms/ProductForm.jsx]
 * Data: 05/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [ADICIONADO] Carregamento de fornecedores (`getAllSuppliers`).
 * - [FEAT] Geração automática de SKU baseada na fórmula: Categoria + Fornecedor + Sequencial.
 * - [UI] Novo campo "Fornecedor" ao lado de "Categoria".
 */
//

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Loader2,
  Camera,
  Heart,
  Ruler,
  X,
  Search,
  MapPin,
  Trash2,
  Plus,
  Sparkles,
} from 'lucide-react';
import productService from '../../services/productService';

const ProductForm = ({
  productData,
  onClose,
  onSave,
  maxSeparamos = 5,
  currentRecommendedCount = 0,
}) => {
  // -------------------- CONFIG: PRECIFICAÇÃO --------------------
  const TARGET_MARGIN = 0.65;
  const PACKAGING_COST = 1.5;

  // -------------------- STATE: PRODUTO (PAI) --------------------
  const initialData = productData || {};

  const [product, setProduct] = useState(() => {
    return {
      supplierItemCode: initialData.supplierItemCode || '',
      supplierProductName: initialData.supplierProductName || '',
      name: initialData.name || '',
      description: initialData.description || '',
      sku: initialData.sku || '',
      productType: initialData.productType || 'venda',
      price: initialData.price || 0.0,
      cost: initialData.cost || 0.0,
      stock: initialData.stock || 0,
      isActive: initialData.isActive ?? true,
      displaySettings: initialData.displaySettings || {
        onHomepage: false,
        onSeparamos: false,
      },
      dimensions: initialData.dimensions || {
        height: 0,
        length: 0,
        width: 0,
        weight: 0,
      },
      images: initialData.images || [],
      id: initialData.id || null,
      rentalGroups: initialData.rentalGroups || [],
      location: initialData.location || 'Em Loja',
      status: initialData.status || 'Disponível',
      variants: initialData.variants || [],
      categoryId: initialData.categoryId || '',
      supplierId: initialData.supplierId || '', // [GS-NOVO]
    };
  });

  const [priceInputValue, setPriceInputValue] = useState('0,00');
  const [costInputValue, setCostInputValue] = useState('0,00');

  const [variants, setVariants] = useState(() => {
    const base = productData?.variants || [];
    const validVariants = Array.isArray(base) ? base : [];

    return validVariants.map((v) => ({
      name: v.name || '',
      skuVariant: v.skuVariant || '',
      quantity: Number(v.quantity || 0),
      price: Number(v.price || 0),
      priceDisplay:
        typeof v.price === 'number'
          ? v.price.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : '0,00',
      images: Array.isArray(v.images) ? v.images : [],
      supplierProductName:
        v.supplierProductName || productData?.supplierProductName || '',
      id: v.id || null,
    }));
  });

  const [variantNewFiles, setVariantNewFiles] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSupplierProduct, setIsLoadingSupplierProduct] = useState(false);
  const [newImageFiles, setNewImageFiles] = useState([]);

  const fileInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const hasWarnedZeroPriceRef = useRef(false);

  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // [GS-NOVO]
  const [isLoadingData, setIsLoadingData] = useState(true); // [GS-NOVO] Unificado

  const baseLocationOptions = ['Em Loja', 'Em Estoque', 'Em Banho'];
  const locationOptions = baseLocationOptions.includes(product.location)
    ? baseLocationOptions
    : [product.location, ...baseLocationOptions];

  // -------------------- HELPERS --------------------
  const formatNumberToCurrencyBR = (num) =>
    Number(num || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const maskCurrencyToNumber = (raw) => {
    let v = String(raw).replace(/\D/g, '');
    if (v === '') return 0;

    v = v.padStart(3, '0');
    const intPart = v.slice(0, -2);
    const decPart = v.slice(-2);

    return parseFloat(`${intPart}.${decPart}`);
  };

  const suggestedPrice = useMemo(() => {
    const c = Number(product.cost) || 0;
    if (c <= 0) return 0;

    const denominator = 1 - TARGET_MARGIN;
    if (denominator <= 0) return 0;

    const raw = (c + PACKAGING_COST) / denominator;
    const rounded = Math.round(raw * 100) / 100;
    return Number.isFinite(rounded) ? rounded : 0;
  }, [product.cost, TARGET_MARGIN, PACKAGING_COST]);

  const isPriceZero = (Number(product.price) || 0) <= 0;
  const isBelowCost =
    !isPriceZero && (Number(product.price) || 0) < (Number(product.cost) || 0);

  // -------------------- EFFECTS --------------------
  useEffect(() => {
    if (productData && typeof productData.price === 'number') {
      setPriceInputValue(formatNumberToCurrencyBR(productData.price));
    } else {
      setPriceInputValue('0,00');
    }

    if (productData && typeof productData.cost === 'number') {
      setCostInputValue(formatNumberToCurrencyBR(productData.cost));
    } else {
      setCostInputValue('0,00');
    }
  }, [productData]);

  useEffect(() => {
    if (priceInputRef.current && priceInputValue) {
      const input = priceInputRef.current;
      input.setSelectionRange(input.value.length, input.value.length);
    }
  }, [priceInputValue]);

  // [GS-NOVO] Carregamento de Categorias e Fornecedores
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setIsLoadingData(true);
        const [cats, sups] = await Promise.all([
          productService.getAllCategories(),
          productService.getAllSuppliers(),
        ]);

        if (!isMounted) return;

        const sortedCats = Array.isArray(cats)
          ? [...cats].sort((a, b) =>
              String(a.name || '').localeCompare(String(b.name || ''))
            )
          : [];

        const sortedSups = Array.isArray(sups)
          ? [...sups].sort((a, b) =>
              String(a.name || '').localeCompare(String(b.name || ''))
            )
          : [];

        setCategories(sortedCats);
        setSuppliers(sortedSups);
      } catch (error) {
        console.error('Erro ao carregar dados auxiliares:', error);
        if (isMounted) {
          setCategories([]);
          setSuppliers([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingData(false);
        }
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  // [GS-NOVO] Geração Dinâmica de SKU (Fórmula: Categoria + Fornecedor + Sequencial)
  useEffect(() => {
    const autoGenerateSku = async () => {
      // Executa apenas se for NOVO produto (!product.id) ou SKU estiver vazio
      // e se tivermos Categoria e Fornecedor selecionados.
      if (
        product.categoryId &&
        product.supplierId &&
        (!product.id || !product.sku)
      ) {
        try {
          const smartSku = await productService.generateSmartSku(
            product.categoryId,
            product.supplierId
          );
          setProduct((prev) => ({ ...prev, sku: smartSku }));
        } catch (err) {
          console.error('Erro ao gerar SKU dinâmico:', err);
        }
      }
    };
    autoGenerateSku();
  }, [product.categoryId, product.supplierId, product.id, product.sku]);

  // -------------------- HANDLERS: PRODUTO --------------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setProduct((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDisplayChange = (e) => {
    const { name, checked } = e.target;

    if (name === 'onSeparamos' && checked) {
      const alreadyMarked = !!product.displaySettings?.onSeparamos;

      const effectiveCount =
        (typeof currentRecommendedCount === 'number'
          ? currentRecommendedCount
          : 0) - (alreadyMarked ? 1 : 0);

      if (effectiveCount >= maxSeparamos) {
        alert(
          `Limite atingido: já existem ${maxSeparamos} produtos marcados como "Recomendado (Separamos para Você)". Desmarque outro produto antes de marcar um novo.`
        );
        return;
      }
    }

    setProduct((prev) => ({
      ...prev,
      displaySettings: {
        ...prev.displaySettings,
        [name]: checked,
      },
    }));
  };

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseFloat(String(value).replace(',', '.')) || 0;

    setProduct((prev) => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [name]: numericValue,
      },
    }));
  };

  const handleCostChange = (e) => {
    const rawDigits = e.target.value.replace(/[^\d]/g, '').padStart(3, '0');
    const integerPart = rawDigits.slice(0, -2);
    const decimalPart = rawDigits.slice(-2);

    const integerNumber = parseInt(integerPart, 10) || 0;
    const formattedDisplay = `${integerNumber.toLocaleString(
      'pt-BR'
    )},${decimalPart}`;

    setCostInputValue(formattedDisplay);
    setProduct((prev) => ({
      ...prev,
      cost: parseFloat(`${integerNumber}.${decimalPart}`),
    }));
  };

  const handlePriceChange = (e) => {
    const rawDigits = e.target.value.replace(/[^\d]/g, '').padStart(3, '0');
    const integerPart = rawDigits.slice(0, -2);
    const decimalPart = rawDigits.slice(-2);

    const integerNumber = parseInt(integerPart, 10) || 0;

    const formattedDisplay = `${integerNumber.toLocaleString(
      'pt-BR'
    )},${decimalPart}`;

    const nextPrice = parseFloat(`${integerNumber}.${decimalPart}`);

    setPriceInputValue(formattedDisplay);
    setProduct((prev) => ({
      ...prev,
      price: nextPrice,
    }));

    if (nextPrice > 0) {
      hasWarnedZeroPriceRef.current = false;
    }
  };

  const handlePriceBlur = () => {
    const p = Number(product.price) || 0;
    const c = Number(product.cost) || 0;

    if (p <= 0 && c > 0 && !hasWarnedZeroPriceRef.current) {
      alert(
        'Atenção: o preço de venda está zerado. Se você salvar assim, o produto ficará em "Aguardando Precificação" e não aparecerá no PDV.'
      );
      hasWarnedZeroPriceRef.current = true;
    }
  };

  const applySuggestedPrice = () => {
    if ((Number(product.cost) || 0) <= 0) {
      alert('Defina o custo do produto para calcular a sugestão de venda.');
      return;
    }
    if (suggestedPrice <= 0) {
      alert('Não foi possível calcular a sugestão. Verifique o custo informado.');
      return;
    }

    setProduct((prev) => ({
      ...prev,
      price: suggestedPrice,
    }));
    setPriceInputValue(formatNumberToCurrencyBR(suggestedPrice));
    hasWarnedZeroPriceRef.current = false;
  };

  const handleRentalGroupChange = (e) => {
    const { value, checked } = e.target;

    setProduct((prev) => {
      let newRentalGroups = [...prev.rentalGroups];

      if (checked) {
        if (!newRentalGroups.includes(value)) {
          newRentalGroups.push(value);
        }
      } else {
        newRentalGroups = newRentalGroups.filter((g) => g !== value);
      }

      return {
        ...prev,
        rentalGroups: newRentalGroups,
      };
    });
  };

  const handleImageSelect = (e) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const totalImages = product.images.length + newImageFiles.length;
      const remaining = 4 - totalImages;

      if (remaining <= 0) {
        e.target.value = null;
        return;
      }

      const filesToTake = filesArray.slice(0, remaining);
      setNewImageFiles((prev) => [...prev, ...filesToTake]);
    }

    e.target.value = null;
  };

  const handleRemoveExistingImage = (idx) => {
    setProduct((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx),
    }));
  };

  const handleRemoveNewImage = (idx) => {
    setNewImageFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const fetchSupplierProduct = useCallback(async () => {
    const supplierItemCode = (product.supplierItemCode || '').trim();
    if (!supplierItemCode) return;

    setIsLoadingSupplierProduct(true);

    try {
      const supplierProductData =
        await productService.getProductBySupplierItemCode(supplierItemCode);

      if (supplierProductData) {
        setProduct((prev) => ({
          ...prev,
          name: supplierProductData.name || '',
          supplierProductName:
            supplierProductData.supplierProductName ||
            prev.supplierProductName ||
            '',
          sku: supplierProductData.sku || '',
          stock: supplierProductData.stock || 0,
          cost: supplierProductData.cost || 0.0,
          description: supplierProductData.description || '',
          id: supplierProductData.id || null,
          rentalGroups: supplierProductData.rentalGroups || [],
          location: supplierProductData.location || 'Em Loja',
          status: supplierProductData.status || 'Disponível',
          displaySettings:
            supplierProductData.displaySettings ||
            prev.displaySettings || {
              onHomepage: false,
              onSeparamos: false,
            },
          categoryId:
            supplierProductData.categoryId ||
            prev.categoryId ||
            initialData.categoryId ||
            '',
          supplierId:
            supplierProductData.supplierId || prev.supplierId || '', // [GS-NOVO]
        }));

        if (typeof supplierProductData.price === 'number') {
          setPriceInputValue(formatNumberToCurrencyBR(supplierProductData.price));
          setProduct((prev) => ({
            ...prev,
            price: supplierProductData.price,
          }));
        }

        if (typeof supplierProductData.cost === 'number') {
          setCostInputValue(formatNumberToCurrencyBR(supplierProductData.cost));
        } else {
          setCostInputValue(formatNumberToCurrencyBR(0));
        }
      } else {
        alert(
          'Produto com este código de fornecedor não encontrado. Preencha manualmente.'
        );

        setProduct((prev) => ({
          ...prev,
          name: '',
          sku: '',
          stock: 0,
          cost: 0.0,
          description: '',
          id: null,
          rentalGroups: [],
          location: 'Em Loja',
          status: 'Disponível',
          displaySettings: {
            onHomepage: false,
            onSeparamos: false,
          },
          categoryId: prev.categoryId || initialData.categoryId || '',
          supplierId: prev.supplierId || '',
        }));

        setPriceInputValue('0,00');
        setCostInputValue('0,00');
        hasWarnedZeroPriceRef.current = false;
      }
    } catch (error) {
      console.error('Erro ao buscar produto do fornecedor:', error);
      alert(
        'Erro ao buscar o produto do fornecedor. Veja o console para detalhes.'
      );
    } finally {
      setIsLoadingSupplierProduct(false);
    }
  }, [product.supplierItemCode, initialData.categoryId, initialData.supplierId]);

  const handleSupplierCodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchSupplierProduct();
    }
  };

  // -------------------- HANDLERS: VARIANTES --------------------
  const addVariant = () => {
    setVariants((prev) => [
      ...prev,
      {
        name: '',
        skuVariant: '',
        quantity: 1,
        price: 0,
        priceDisplay: '0,00',
        images: [],
        supplierProductName: product.supplierProductName || '',
        id: null,
      },
    ]);
  };

  const removeVariant = (index) => {
    setVariants((prev) => prev.filter((_, i) => i !== index));

    setVariantNewFiles((prev) => {
      const clone = { ...prev };
      delete clone[index];
      return clone;
    });
  };

  const handleVariantField = (index, field, value) => {
    setVariants((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleVariantPrice = (index, e) => {
    const raw = e.target.value;
    const num = maskCurrencyToNumber(raw);
    const display = formatNumberToCurrencyBR(num);

    setVariants((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        price: num,
        priceDisplay: display,
      };
      return copy;
    });
  };

  const handleVariantImages = (index, files) => {
    const filesArray = Array.from(files || []);
    const current = variants[index]?.images?.length || 0;
    const currentNew = variantNewFiles[index]?.length || 0;
    const remaining = 4 - (current + currentNew);

    if (remaining <= 0) return;

    const toTake = filesArray.slice(0, remaining);

    setVariantNewFiles((prev) => ({
      ...prev,
      [index]: [...(prev[index] || []), ...toTake],
    }));
  };

  const removeVariantExistingImage = (vIdx, imgIdx) => {
    setVariants((prev) => {
      const copy = [...prev];
      copy[vIdx] = {
        ...copy[vIdx],
        images: copy[vIdx].images.filter((_, i) => i !== imgIdx),
      };
      return copy;
    });
  };

  const removeVariantNewImage = (vIdx, fileIdx) => {
    setVariantNewFiles((prev) => {
      const arr = [...(prev[vIdx] || [])];
      arr.splice(fileIdx, 1);
      return {
        ...prev,
        [vIdx]: arr,
      };
    });
  };

  const generateSku = (prefix = 'GS-PROD-') => {
    return `${prefix}${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 4)
      .toUpperCase()}`;
  };

  // -------------------- SUBMIT --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const categoryId = product.categoryId;
    if (!categoryId || String(categoryId).trim() === '') {
      alert('Selecione uma categoria antes de salvar o produto.');
      return;
    }

    setIsSaving(true);

    try {
      let finalImageUrls = [...product.images];

      if (newImageFiles.length > 0) {
        const uploaded = await Promise.all(
          newImageFiles.map((file) => productService.uploadImage(file))
        );
        finalImageUrls = [...finalImageUrls, ...uploaded];
      }

      const finalProductSku =
        product.sku && product.sku.trim() !== '' ? product.sku : generateSku();

      const numericPrice = Number(product.price) || 0;
      let finalStatus = product.status || 'Disponível';

      if (numericPrice <= 0) {
        finalStatus = 'Aguardando Precificação';
      }

      if (numericPrice > 0 && finalStatus === 'Aguardando Precificação') {
        finalStatus = 'Disponível';
      }

      const baseData = {
        ...product,
        sku: finalProductSku,
        images: finalImageUrls,
        isActive: true,
        supplierProductName: product.supplierProductName || product.name || '',
        status: finalStatus,
        categoryId,
      };

      let dataToSave = baseData;
      if (product.id) {
        const { stock, ...rest } = baseData;
        dataToSave = rest;
      }

      const savedProduct = await onSave(dataToSave, product.id);

      if (savedProduct && savedProduct.sku) {
        if (variants.length > 0) {
          const existingVariants =
            await productService.getVariantsByParentSku(savedProduct.sku);

          let nextIndex = existingVariants.length + 1;

          for (let i = 0; i < variants.length; i++) {
            const v = variants[i];

            if (!v) {
              console.warn(`Variante no índice ${i} é inválida. Pulando.`);
              continue;
            }

            const pendingFiles = variantNewFiles[i] || [];
            let finalVariantImages = [...(v.images || [])];

            if (pendingFiles.length > 0) {
              const uploaded = await Promise.all(
                pendingFiles.map((file) => productService.uploadImage(file))
              );
              finalVariantImages = [...finalVariantImages, ...uploaded];
            }

            const variantCategoryId =
              (savedProduct.categoryId && String(savedProduct.categoryId)) ||
              categoryId ||
              null;
            const variantSupplierId =
              (savedProduct.supplierId && String(savedProduct.supplierId)) ||
              product.supplierId ||
              null;

            const variantData = {
              ...v,
              sku: v.skuVariant || `${savedProduct.sku}-${nextIndex}`,
              parentSku: savedProduct.sku,
              isVariant: true,
              images: finalVariantImages,
              productType: savedProduct.productType,
              location: savedProduct.location,
              status: savedProduct.status,
              rentalGroups: savedProduct.rentalGroups,
              dimensions: savedProduct.dimensions,
              displaySettings: savedProduct.displaySettings,
              supplierProductName:
                v.supplierProductName || savedProduct.supplierProductName || '',
              categoryId: variantCategoryId,
              supplierId: variantSupplierId,
            };

            await onSave(variantData, v.id);
            nextIndex++;
          }
        }
      } else {
        throw new Error(
          'Falha ao salvar o produto principal. As variantes não podem ser processadas.'
        );
      }

      onClose();

      if (typeof window.location.reload === 'function') {
        window.location.reload();
      }
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      alert('Ocorreu um erro ao salvar o produto. Verifique o console.');
    } finally {
      setIsSaving(false);
    }
  };

  // -------------------- RENDER --------------------
  const maxImages = 4;
  const currentImageCount = product.images.length + newImageFiles.length;
  const placeholderCount = Math.max(0, maxImages - currentImageCount);

  const priceInputBorderClass = isBelowCost
    ? 'border-red-400 ring-1 ring-red-200'
    : isPriceZero && (Number(product.cost) || 0) > 0
    ? 'border-orange-400 ring-1 ring-orange-200'
    : 'border-gray-300';

  return (
    <form
      onSubmit={handleSubmit}
      className="p-1 flex flex-col h-full"
      noValidate
    >
      <div className="flex-grow overflow-y-auto pr-2 pb-4 bg-white text-gray-900 rounded-md">
        <div className="mb-4 p-2">
          <label
            htmlFor="supplierItemCode"
            className="block text-sm font-medium text-gray-700"
          >
            Código do Produto do Fornecedor
          </label>

          <div className="relative mt-1">
            <input
              type="text"
              id="supplierItemCode"
              name="supplierItemCode"
              value={product.supplierItemCode}
              onChange={handleChange}
              onKeyDown={handleSupplierCodeKeyDown}
              className="w-full bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold text-base font-semibold pr-10"
              placeholder="Insira o código do fornecedor para pré-preencher"
              autoFocus
            />

            <button
              type="button"
              onClick={fetchSupplierProduct}
              disabled={
                isLoadingSupplierProduct || !product.supplierItemCode.trim()
              }
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-yellow-600 disabled:opacity-50"
              title="Buscar produto do fornecedor"
            >
              {isLoadingSupplierProduct ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Search className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 px-2">
          <div className="md:col-span-2 space-y-4">
            <div>
              <label
                htmlFor="supplierProductName"
                className="block text-sm font-medium text-gray-700"
              >
                Nome real no fornecedor (nota)
              </label>
              <input
                type="text"
                id="supplierProductName"
                name="supplierProductName"
                value={product.supplierProductName}
                onChange={handleChange}
                className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold text-base"
                placeholder="Ex.: Anel Dourado Meia-Lua (como vem na nota)"
              />
            </div>

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Nome do Produto{' '}
                <span className="text-gray-500 text-xs">(para venda)</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={product.name}
                onChange={handleChange}
                className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold text-base font-semibold"
                placeholder="Nome do produto"
              />
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                Descrição Geral
              </label>
              <textarea
                id="description"
                name="description"
                value={product.description}
                onChange={handleChange}
                rows="5"
                className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold"
                placeholder="Detalhes do produto, materiais, inspiração..."
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  SKU (Código)
                </label>
                <p className="text-sm font-mono bg-gray-50 p-2 rounded-md mt-1 h-[40px] flex items-center border border-gray-200 text-gray-600">
                  {product.sku || 'Será gerado automaticamente...'}
                </p>
              </div>

              <div>
                <label
                  htmlFor="productType"
                  className="block text-sm font-medium text-gray-700"
                >
                  Tipo
                </label>
                <select
                  id="productType"
                  name="productType"
                  value={product.productType}
                  onChange={handleChange}
                  className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold capitalize h-[40px]"
                >
                  <option value="venda">Venda</option>
                  <option value="clube">Clube</option>
                  <option value="venda_clube">Venda/Clube</option>
                </select>
              </div>
            </div>

            {/* [GS-NOVO] Linha Dividida: Categoria e Fornecedor */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              <div>
                <label
                  htmlFor="categoryId"
                  className="block text-sm font-medium text-gray-700"
                >
                  Categoria <span className="text-red-500">*</span>
                </label>
                <select
                  id="categoryId"
                  name="categoryId"
                  value={product.categoryId}
                  onChange={handleChange}
                  disabled={isLoadingData || categories.length === 0}
                  className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold text-sm"
                >
                  <option value="">
                    {isLoadingData
                      ? 'Carregando...'
                      : 'Selecione uma categoria'}
                  </option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.code || cat.id}>
                      {cat.name || cat.code || '(Sem nome)'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="supplierId"
                  className="block text-sm font-medium text-gray-700"
                >
                  Fornecedor <span className="text-red-500">*</span>
                </label>
                <select
                  id="supplierId"
                  name="supplierId"
                  value={product.supplierId}
                  onChange={handleChange}
                  disabled={isLoadingData || suppliers.length === 0}
                  className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold text-sm"
                >
                  <option value="">
                    {isLoadingData
                      ? 'Carregando...'
                      : 'Selecione um fornecedor'}
                  </option>
                  {suppliers.map((sup) => (
                    <option key={sup.id} value={sup.id}>
                      {sup.name || '(Sem nome)'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Estoque
                </label>
                <p className="text-lg font-bold text-gray-900 bg-gray-50 p-2 rounded-md mt-1 text-center h-[48px] flex items-center justify-center border border-gray-200">
                  {product.stock}
                </p>
              </div>

              <div>
                <label
                  htmlFor="cost"
                  className="block text-sm font-medium text-gray-700"
                >
                  Custo (R$)
                  <span className="text-gray-500 text-xs"> (NF)</span>
                </label>
                <input
                  type="text"
                  id="cost"
                  name="cost"
                  value={costInputValue}
                  onChange={handleCostChange}
                  className="w-full mt-1 text-lg font-bold text-red-600 bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold text-center"
                  inputMode="numeric"
                  placeholder="0,00"
                />
                <p className="mt-1 text-[11px] text-gray-500 text-center">
                  Considera embalagem padrão de R$ {formatNumberToCurrencyBR(PACKAGING_COST)} na sugestão.
                </p>
              </div>

              <div>
                <label
                  htmlFor="price"
                  className="block text-sm font-medium text-gray-700"
                >
                  Venda (R$)
                </label>

                <input
                  type="text"
                  id="price"
                  name="price"
                  value={priceInputValue}
                  onChange={handlePriceChange}
                  onBlur={handlePriceBlur}
                  className={`w-full mt-1 text-lg font-bold text-green-600 bg-white p-2 rounded-md ${priceInputBorderClass} focus:ring-brand-gold focus:border-brand-gold text-center`}
                  inputMode="numeric"
                  ref={priceInputRef}
                />

                <div className="mt-1 flex items-center justify-center gap-2">
                  <span className="text-[11px] text-gray-500">
                    Sugestão (Margem 65%):
                  </span>
                  <span className="text-[11px] font-bold text-gray-800">
                    R$ {formatNumberToCurrencyBR(suggestedPrice)}
                  </span>
                  <button
                    type="button"
                    onClick={applySuggestedPrice}
                    title="Aplicar sugestão no preço de venda"
                    className="p-1 rounded hover:bg-yellow-100 text-yellow-700"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                </div>

                {isBelowCost && (
                  <p className="mt-1 text-[11px] text-red-600 text-center font-semibold">
                    Atenção: venda abaixo do custo.
                  </p>
                )}

                {isPriceZero && (Number(product.cost) || 0) > 0 && (
                  <p className="mt-1 text-[11px] text-orange-600 text-center font-semibold">
                    Preço zerado: ao salvar, o status será “Aguardando Precificação”.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label
                htmlFor="location"
                className="block text-sm font-medium text-gray-700 flex items-center mb-1"
              >
                <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                Localização
              </label>
              <select
                id="location"
                name="location"
                value={product.location}
                onChange={handleChange}
                className="w-full bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold text-sm text-gray-900"
              >
                {locationOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700"
              >
                Status do Produto
              </label>
              <select
                id="status"
                name="status"
                value={product.status}
                onChange={handleChange}
                className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 focus:ring-brand-gold focus:border-brand-gold text-sm text-gray-900"
              >
                <option value="Aguardando Precificação">
                  Aguardando Precificação (não aparece no PDV)
                </option>
                <option value="Disponível">Disponível (aparece no PDV)</option>
                <option value="Reservado">Reservado</option>
                <option value="Alugado">Alugado</option>
                <option value="Vendido">Vendido</option>
                <option value="Indisponível">Indisponível</option>
                <option value="Em manutenção">Em manutenção</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Apenas produtos com status "Disponível" são exibidos na tela de vendas (PDV).
                Se o preço estiver zerado, o sistema força "Aguardando Precificação" ao salvar.
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grupos de Aluguel (Selecione todos que se aplicam)
              </label>
              <div className="space-y-2">
                {[
                  {
                    key: 'C',
                    label: 'Grupo C (Bronze, Prata, Ouro, Avulso)',
                  },
                  { key: 'B', label: 'Grupo B (Prata, Ouro)' },
                  { key: 'A', label: 'Grupo A (Ouro)' },
                ].map((g) => (
                  <label
                    key={g.key}
                    className="flex items-center bg-white border border-gray-300 p-3 rounded-md cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      name="rentalGroups"
                      value={g.key}
                      checked={product.rentalGroups.includes(g.key)}
                      onChange={handleRentalGroupChange}
                      className="h-4 w-4 text-yellow-600 focus:ring-yellow-600 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-gray-800">{g.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 mt-4">
              <h3 className="text-base font-semibold text-gray-800 flex items-center mb-2">
                <Ruler className="h-5 w-5 mr-2 text-blue-500" />
                Dimensões e Peso
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { id: 'height', label: 'Altura (cm)' },
                  { id: 'length', label: 'Comprimento (cm)' },
                  { id: 'width', label: 'Largura (cm)' },
                  { id: 'weight', label: 'Peso (g)' },
                ].map((f) => (
                  <div key={f.id}>
                    <label
                      htmlFor={f.id}
                      className="block text-xs font-medium text-gray-600"
                    >
                      {f.label}
                    </label>
                    <input
                      type="number"
                      id={f.id}
                      name={f.id}
                      value={product.dimensions[f.id]}
                      onChange={handleDimensionChange}
                      className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 text-center"
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-semibold text-gray-800">
                  Variantes (opcional)
                </h3>
                <button
                  type="button"
                  onClick={addVariant}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-yellow-500 text-black hover:bg-yellow-400"
                >
                  <Plus className="h-4 w-4" />
                  Adicionar variante
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-3">
                Cadastre peças do mesmo fornecedor como itens individuais
                (nome / preço / estoque / imagens).
              </p>

              <div className="space-y-4">
                {variants.length === 0 && (
                  <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-md p-4">
                    Nenhuma variante adicionada.
                  </div>
                )}

                {variants.map((v, idx) => (
                  <div
                    key={`variant-${idx}`}
                    className="border border-gray-300 rounded-lg p-4 bg-white"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-800">
                        Variante #{idx + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => removeVariant(idx)}
                        className="text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remover
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Nome da Variante
                        </label>
                        <input
                          type="text"
                          value={v.name}
                          onChange={(e) =>
                            handleVariantField(idx, 'name', e.target.value)
                          }
                          className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300"
                          placeholder="Ex.: Colar XYZ — Var. 1"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          SKU (variantes)
                        </label>
                        <input
                          type="text"
                          value={v.skuVariant}
                          onChange={(e) =>
                            handleVariantField(idx, 'skuVariant', e.target.value)
                          }
                          className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300"
                          placeholder="Ex.: ABC123-1"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Quantidade
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={v.quantity}
                          onChange={(e) =>
                            handleVariantField(
                              idx,
                              'quantity',
                              Number(e.target.value || 0)
                            )
                          }
                          className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 text-center"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Preço de Venda (R$)
                        </label>
                        <input
                          type="text"
                          value={v.priceDisplay}
                          onChange={(e) => handleVariantPrice(idx, e)}
                          className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300 text-center font-semibold text-green-700"
                          inputMode="numeric"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-600">
                          Imagens da Variante (até 4)
                        </label>

                        <div className="mt-1 grid grid-cols-4 gap-2">
                          {v.images.map((url, i) => (
                            <div
                              key={`${url}-${i}`}
                              className="relative group aspect-square border border-gray-200 rounded-md overflow-hidden"
                            >
                              <img
                                src={url}
                                alt={`Var ${idx} Img ${i}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeVariantExistingImage(idx, i)}
                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}

                          {(variantNewFiles[idx] || []).map((file, i) => (
                            <div
                              key={`${file.name}-${i}`}
                              className="relative group aspect-square border border-gray-200 rounded-md overflow-hidden"
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`New Var ${idx} ${i}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeVariantNewImage(idx, i)}
                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}

                          {Array.from({
                            length: Math.max(
                              0,
                              4 - (v.images.length + (variantNewFiles[idx]?.length || 0))
                            ),
                          }).map((_, ph) => (
                            <label
                              key={`ph-${ph}`}
                              className="aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-yellow-500"
                            >
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="hidden"
                                onChange={(e) =>
                                  handleVariantImages(idx, e.target.files)
                                }
                              />
                              <Camera className="h-6 w-6 text-gray-400" />
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-600">
                        Nome real no fornecedor (nota) — variante
                      </label>
                      <input
                        type="text"
                        value={v.supplierProductName || ''}
                        onChange={(e) =>
                          handleVariantField(
                            idx,
                            'supplierProductName',
                            e.target.value
                          )
                        }
                        className="w-full mt-1 bg-white p-2 rounded-md border border-gray-300"
                        placeholder="(Opcional) Caso difira do produto-pai"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Galeria de Imagens (Produto-pai)
              </label>
              <input
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp"
                ref={fileInputRef}
                onChange={handleImageSelect}
                className="hidden"
              />

              <div className="grid grid-cols-2 gap-2">
                {product.images.map((url, index) => (
                  <div
                    key={url}
                    className="relative group aspect-square border border-gray-200 rounded-md overflow-hidden"
                  >
                    <img
                      src={url}
                      alt={`Imagem do produto ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingImage(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}

                {newImageFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="relative group aspect-square border border-gray-200 rounded-md overflow-hidden"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Nova imagem ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveNewImage(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}

                {Array.from({ length: placeholderCount }).map((_, index) => (
                  <div
                    key={`placeholder-${index}`}
                    className="aspect-square bg-white rounded-md flex items-center justify-center border-2 border-dashed border-gray-300 cursor-pointer hover:border-yellow-500"
                    onClick={() =>
                      fileInputRef.current && fileInputRef.current.click()
                    }
                  >
                    <Camera className="h-8 w-8 text-gray-400" />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status e Visibilidade
              </label>

              <div className="space-y-2">
                <label className="flex items-center justify-between bg-white border border-gray-300 p-3 rounded-md cursor-pointer hover:bg-gray-50">
                  <div className="flex items-center">
                    <Heart className="h-5 w-5 mr-3 text-pink-500" />
                    <span className="font-semibold text-gray-800">
                      Recomendado (Separamos para Você)
                    </span>
                  </div>

                  <input
                    type="checkbox"
                    name="onSeparamos"
                    checked={!!product.displaySettings.onSeparamos}
                    onChange={handleDisplayChange}
                    className="h-5 w-5"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-200 flex-shrink-0 bg-white">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md mr-4 border border-gray-300"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSaving || isLoadingSupplierProduct}
          className="bg-yellow-500 text-black font-bold py-2 px-6 rounded-lg hover:bg-yellow-400 disabled:bg-gray-400 flex items-center"
        >
          {isSaving && <Loader2 className="animate-spin mr-2" />}
          Salvar Produto
        </button>
      </div>
    </form>
  );
};

export default ProductForm;